import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, CartesianGrid, Legend,
} from "recharts";
import {
  DollarSign, TrendingUp, Building2, Target, ClipboardList,
  Plus, X, Calendar, MapPin, Tag, Award, Funnel,
} from "lucide-react";
import { API, useAuth } from "../../App";
import { useToast } from "@/hooks/use-toast";

const RANGES = [
  { key: "7d", label: "7 days" },
  { key: "30d", label: "30 days" },
  { key: "90d", label: "90 days" },
];

const STATUS_BADGES = {
  pipeline:  { label: "Pipeline",  cls: "bg-amber-100 text-amber-700" },
  signed:    { label: "Signed",    cls: "bg-emerald-100 text-emerald-700" },
  paid:      { label: "Paid",      cls: "bg-blue-100 text-blue-700" },
  cancelled: { label: "Cancelled", cls: "bg-slate-200 text-slate-600" },
};

const money = (n) =>
  typeof n === "number"
    ? n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 })
    : "$0";

const KpiCard = ({ icon: Icon, label, value, sub, testId, accent = "text-[#0A2342]" }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-white rounded-xl border border-slate-200 p-5"
    data-testid={testId}
  >
    <div className="flex items-center justify-between mb-2">
      <Icon className="w-5 h-5 text-slate-400" />
      <span className="text-xs uppercase tracking-wide text-slate-400">{label}</span>
    </div>
    <p className={`text-2xl font-bold ${accent}`}>{value}</p>
    {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
  </motion.div>
);

const BreakdownCard = ({ title, icon: Icon, rows, testId }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid={testId}>
    <div className="flex items-center gap-2 mb-4">
      <Icon className="w-4 h-4 text-[#DE6E4B]" />
      <h3 className="font-semibold text-[#0A2342]">{title}</h3>
    </div>
    {(!rows || rows.length === 0) ? (
      <p className="text-sm text-slate-400">No data in this range yet.</p>
    ) : (
      <ul className="space-y-2">
        {rows.map((r) => (
          <li key={r.key} className="flex items-center justify-between text-sm">
            <span className="text-slate-700 truncate pr-3" title={r.key}>{r.key}</span>
            <span className="font-mono text-right">
              <span className="text-emerald-600 font-semibold">{money(r.commission)}</span>
              <span className="text-slate-400 ml-2">· {r.leases}</span>
            </span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

const FunnelStep = ({ label, value, rate, testId }) => (
  <div className="text-center" data-testid={testId}>
    <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
    <p className="text-2xl font-bold text-[#0A2342] mt-1">{value}</p>
    {rate !== undefined && (
      <p className="text-xs text-slate-400 mt-1">{rate}% conv</p>
    )}
  </div>
);

const LeaseDashboard = () => {
  const { token } = useAuth();
  const { toast } = useToast();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [range, setRange] = useState("30d");
  const [dash, setDash] = useState(null);
  const [leases, setLeases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    lead_id: "",
    property_name: "",
    lease_date: new Date().toISOString().slice(0, 10),
    commission_amount: "",
    monthly_rent: "",
    lease_term_months: "12",
    status: "signed",
    source: "",
    neighborhood: "",
    article: "",
    campaign: "",
    notes: "",
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [dashRes, listRes] = await Promise.all([
        axios.get(`${API}/admin/revenue-dashboard?range_key=${range}`, { headers }),
        axios.get(`${API}/leases?limit=50`, { headers }),
      ]);
      setDash(dashRes.data);
      setLeases(listRes.data.items || []);
    } catch (err) {
      console.error("revenue-dashboard fetch failed", err);
      toast({ title: "Failed to load revenue data", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [range, headers, toast]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.lead_id.trim() || !form.property_name.trim()) {
      toast({ title: "Lead ID and Property are required", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        commission_amount: form.commission_amount ? Number(form.commission_amount) : null,
        monthly_rent: form.monthly_rent ? Number(form.monthly_rent) : null,
        lease_term_months: form.lease_term_months ? Number(form.lease_term_months) : null,
      };
      await axios.post(`${API}/leases`, payload, { headers });
      toast({ title: "Lease recorded", description: `${form.property_name} added.` });
      setShowForm(false);
      setForm((f) => ({
        ...f, lead_id: "", property_name: "", commission_amount: "",
        monthly_rent: "", notes: "", source: "", neighborhood: "", article: "", campaign: "",
      }));
      fetchData();
    } catch (err) {
      const msg = err.response?.data?.detail || "Could not save lease.";
      toast({ title: "Save failed", description: String(msg), variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  const totals = dash?.totals || {};
  const funnel = dash?.funnel || {};

  if (loading && !dash) {
    return (
      <div className="flex justify-center py-20" data-testid="lease-dashboard-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0A2342]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="lease-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#0A2342] font-['Playfair_Display']"
              data-testid="lease-dashboard-title">
            Lease Dashboard & Revenue Tracking
          </h1>
          <p className="text-slate-600 text-sm">
            Lead → Consultation → Tour → Lease → Commission · Phase 6
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1">
            {RANGES.map((r) => (
              <button
                key={r.key}
                onClick={() => setRange(r.key)}
                data-testid={`range-${r.key}`}
                className={`px-3 py-1.5 text-sm rounded-md transition ${
                  range === r.key
                    ? "bg-[#0A2342] text-white"
                    : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <button
            onClick={() => setShowForm(true)}
            data-testid="add-lease-button"
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#DE6E4B] text-white rounded-lg hover:bg-[#c75f3f] transition font-medium"
          >
            <Plus className="w-4 h-4" /> Record Lease
          </button>
        </div>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard
          icon={Award}
          label="Leases Signed"
          value={totals.leases_signed ?? 0}
          sub={`+${totals.leases_paid ?? 0} paid`}
          testId="kpi-leases-signed"
        />
        <KpiCard
          icon={DollarSign}
          label="Realised Commission"
          value={money(totals.realised_commission ?? 0)}
          sub={`Avg ${money(totals.avg_commission ?? 0)}`}
          testId="kpi-commission-realised"
          accent="text-emerald-600"
        />
        <KpiCard
          icon={ClipboardList}
          label="Pipeline Commission"
          value={money(totals.pipeline_commission ?? 0)}
          sub={`${totals.leases_pipeline ?? 0} in pipeline`}
          testId="kpi-commission-pipeline"
          accent="text-amber-600"
        />
        <KpiCard
          icon={TrendingUp}
          label="Lead → Lease"
          value={`${totals.lead_to_lease_rate ?? 0}%`}
          sub={`${totals.leads_in_range ?? 0} leads in range`}
          testId="kpi-lead-to-lease"
        />
      </div>

      {/* Funnel */}
      <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="lease-funnel">
        <div className="flex items-center gap-2 mb-4">
          <Funnel className="w-4 h-4 text-[#DE6E4B]" />
          <h3 className="font-semibold text-[#0A2342]">Conversion Funnel</h3>
        </div>
        <div className="grid grid-cols-4 gap-2 items-center">
          <FunnelStep testId="funnel-leads" label="Leads" value={funnel.leads ?? 0} />
          <FunnelStep
            testId="funnel-consultations"
            label="Consultations"
            value={funnel.consultations ?? 0}
            rate={funnel.lead_to_consult_rate}
          />
          <FunnelStep
            testId="funnel-tours"
            label="Tours"
            value={funnel.tours ?? 0}
            rate={funnel.consult_to_tour_rate}
          />
          <FunnelStep
            testId="funnel-leases"
            label="Leases"
            value={funnel.leases ?? 0}
            rate={funnel.tour_to_lease_rate}
          />
        </div>
      </div>

      {/* Monthly Trend */}
      <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="monthly-commission-chart">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-[#DE6E4B]" />
            <h3 className="font-semibold text-[#0A2342]">Monthly Commissions</h3>
          </div>
        </div>
        <div className="h-64">
          {dash?.by_month?.length ? (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={dash.by_month}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="month" stroke="#64748b" fontSize={12} />
                <YAxis yAxisId="left" stroke="#64748b" fontSize={12} />
                <YAxis yAxisId="right" orientation="right" stroke="#64748b" fontSize={12} />
                <Tooltip
                  formatter={(v, name) => (name === "commission" ? money(v) : v)}
                  contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0" }}
                />
                <Legend />
                <Line yAxisId="left" type="monotone" dataKey="commission"
                      stroke="#DE6E4B" strokeWidth={2} dot={{ r: 3 }} name="Commission" />
                <Line yAxisId="right" type="monotone" dataKey="leases"
                      stroke="#0A2342" strokeWidth={2} dot={{ r: 3 }} name="Leases" />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-full text-sm text-slate-400">
              No leases recorded in this range yet.
            </div>
          )}
        </div>
      </div>

      {/* Top Neighborhood bar */}
      <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="neighborhood-bar-chart">
        <div className="flex items-center gap-2 mb-4">
          <MapPin className="w-4 h-4 text-[#DE6E4B]" />
          <h3 className="font-semibold text-[#0A2342]">Revenue by Neighborhood</h3>
        </div>
        <div className="h-56">
          {dash?.by_neighborhood?.length ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={dash.by_neighborhood}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="key" stroke="#64748b" fontSize={11} angle={-20} textAnchor="end" height={60} />
                <YAxis stroke="#64748b" fontSize={12} />
                <Tooltip formatter={(v) => money(v)} contentStyle={{ borderRadius: 8 }} />
                <Bar dataKey="commission" fill="#DE6E4B" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-full text-sm text-slate-400">
              Tag at least one lease with a neighborhood to populate this chart.
            </div>
          )}
        </div>
      </div>

      {/* Breakdown grid */}
      <div className="grid md:grid-cols-3 gap-4">
        <BreakdownCard
          testId="bd-source"
          title="By Lead Source"
          icon={Target}
          rows={dash?.by_source}
        />
        <BreakdownCard
          testId="bd-article"
          title="By Blog Article"
          icon={ClipboardList}
          rows={dash?.by_article}
        />
        <BreakdownCard
          testId="bd-campaign"
          title="By UTM Campaign"
          icon={Tag}
          rows={dash?.by_campaign}
        />
      </div>

      {/* Recent leases table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="recent-leases-table">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2">
          <Building2 className="w-4 h-4 text-[#DE6E4B]" />
          <h3 className="font-semibold text-[#0A2342]">Recent Leases</h3>
          <span className="text-xs text-slate-500 ml-auto">{leases.length} shown</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Date</th>
                <th className="text-left px-4 py-2 font-medium">Property</th>
                <th className="text-left px-4 py-2 font-medium">Lead</th>
                <th className="text-left px-4 py-2 font-medium">Status</th>
                <th className="text-left px-4 py-2 font-medium">Source</th>
                <th className="text-left px-4 py-2 font-medium">Neighborhood</th>
                <th className="text-right px-4 py-2 font-medium">Commission</th>
              </tr>
            </thead>
            <tbody>
              {leases.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center text-slate-400 py-8" data-testid="empty-leases">
                    No leases recorded yet. Click <strong>Record Lease</strong> to begin.
                  </td>
                </tr>
              ) : leases.map((l) => {
                const badge = STATUS_BADGES[l.status] || STATUS_BADGES.signed;
                return (
                  <tr key={l.id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-2 whitespace-nowrap text-slate-700">{(l.lease_date || "").slice(0, 10)}</td>
                    <td className="px-4 py-2 font-medium text-[#0A2342]">{l.property_name}</td>
                    <td className="px-4 py-2 text-slate-600">{l.lead_name || l.lead_email || l.lead_id}</td>
                    <td className="px-4 py-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${badge.cls}`}>{badge.label}</span>
                    </td>
                    <td className="px-4 py-2 text-slate-600">{l.source || "—"}</td>
                    <td className="px-4 py-2 text-slate-600">{l.neighborhood || "—"}</td>
                    <td className="px-4 py-2 text-right font-mono font-semibold text-emerald-600">
                      {money(l.commission_amount || 0)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Record Lease Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="lease-form-modal">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 sticky top-0 bg-white">
              <h2 className="text-lg font-bold text-[#0A2342]">Record New Lease</h2>
              <button
                onClick={() => setShowForm(false)}
                data-testid="close-lease-form"
                className="p-1 text-slate-400 hover:text-slate-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Lead ID *" testId="form-lead-id">
                  <input type="text" required value={form.lead_id}
                         onChange={(e) => setForm({ ...form, lead_id: e.target.value })}
                         className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                         placeholder="paste lead id" />
                </Field>
                <Field label="Property Name *" testId="form-property">
                  <input type="text" required value={form.property_name}
                         onChange={(e) => setForm({ ...form, property_name: e.target.value })}
                         className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                         placeholder="e.g. The Sovereign at Regent Square" />
                </Field>
                <Field label="Lease Date" testId="form-lease-date">
                  <input type="date" value={form.lease_date}
                         onChange={(e) => setForm({ ...form, lease_date: e.target.value })}
                         className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
                </Field>
                <Field label="Status" testId="form-status">
                  <select value={form.status}
                          onChange={(e) => setForm({ ...form, status: e.target.value })}
                          className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm">
                    <option value="pipeline">Pipeline</option>
                    <option value="signed">Signed</option>
                    <option value="paid">Paid</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </Field>
                <Field label="Commission ($)" testId="form-commission">
                  <input type="number" min="0" step="0.01" value={form.commission_amount}
                         onChange={(e) => setForm({ ...form, commission_amount: e.target.value })}
                         className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                         placeholder="e.g. 2400" />
                </Field>
                <Field label="Monthly Rent ($)" testId="form-rent">
                  <input type="number" min="0" step="0.01" value={form.monthly_rent}
                         onChange={(e) => setForm({ ...form, monthly_rent: e.target.value })}
                         className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                         placeholder="e.g. 2200" />
                </Field>
                <Field label="Lease Term (months)" testId="form-term">
                  <input type="number" min="1" value={form.lease_term_months}
                         onChange={(e) => setForm({ ...form, lease_term_months: e.target.value })}
                         className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
                </Field>
                <Field label="Neighborhood (override)" testId="form-neighborhood">
                  <input type="text" value={form.neighborhood}
                         onChange={(e) => setForm({ ...form, neighborhood: e.target.value })}
                         className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                         placeholder="auto-filled from lead" />
                </Field>
                <Field label="Source (override)" testId="form-source">
                  <input type="text" value={form.source}
                         onChange={(e) => setForm({ ...form, source: e.target.value })}
                         className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                         placeholder="auto-filled from lead" />
                </Field>
                <Field label="Campaign (override)" testId="form-campaign">
                  <input type="text" value={form.campaign}
                         onChange={(e) => setForm({ ...form, campaign: e.target.value })}
                         className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                         placeholder="auto-filled from lead" />
                </Field>
              </div>
              <Field label="Notes" testId="form-notes">
                <textarea value={form.notes}
                          onChange={(e) => setForm({ ...form, notes: e.target.value })}
                          rows={3}
                          className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
              </Field>
              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                <button type="button" onClick={() => setShowForm(false)}
                        className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-md">
                  Cancel
                </button>
                <button type="submit" disabled={submitting}
                        data-testid="submit-lease"
                        className="px-4 py-2 text-sm bg-[#DE6E4B] text-white rounded-md hover:bg-[#c75f3f] disabled:opacity-50">
                  {submitting ? "Saving…" : "Save Lease"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const Field = ({ label, children, testId }) => (
  <label className="block" data-testid={testId}>
    <span className="block text-xs font-medium text-slate-600 mb-1">{label}</span>
    {children}
  </label>
);

export default LeaseDashboard;
